import React, { useState, useEffect } from "react";
import axios from "axios";
import { getApiBaseURL } from "../../utils/api";
import { useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import { toast } from "react-toastify";

const UploadDocumentForm = ({ onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [consumerId, setConsumerId] = useState("");
  const [userId, setUserId] = useState("");
  const [authorizationId, setAuthorizationId] = useState("");
  const [notes, setNotes] = useState("");

  const [consumers, setConsumers] = useState([]);
  const [users, setUsers] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const apiBase = getApiBaseURL();

  const { user, tenant_id } = useContext(AuthContext);

  useEffect(() => {
    if (!tenant_id || !user?.user_id || !user?.role || !user?.tenant_type)
      return;

    axios
      .get(`${apiBase}/api/consumers`, {
        params: {
          tenant_id,
          user_id: user.user_id,
          role: user.role,
          tenant_type: user.tenant_type,
        },
      })
      .then((res) => setConsumers(res.data))
      .catch((err) => console.error("Error fetching consumers:", err));

    axios
      .get(`${apiBase}/api/users?tenant_id=${tenant_id}`)
      .then((res) => setUsers(res.data))
      .catch((err) => console.error("Error fetching users:", err));
  }, [tenant_id, user, apiBase]);

  useEffect(() => {
    if (!consumerId || !tenant_id) return;

    axios
      .get(`${apiBase}/api/auths/consumer/${consumerId}?tenant_id=${tenant_id}`)
      .then((res) => setAuthorizations(res.data))
      .catch((err) => console.error("Error fetching authorizations:", err));
  }, [consumerId, tenant_id, apiBase]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("consumer_id", consumerId);
    formData.append("user_id", userId);
    formData.append("authorization_id", authorizationId);
    formData.append("notes", notes);
    formData.append("tenant_id", tenant_id);

    try {
      const response = await axios.post(
        `${apiBase}/api/documents/upload`,
        formData,
        {
          params: { tenant_id },
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Log successful upload
      const uploadedDoc = response.data;
      await auditApi.logActionSafe(
        "UPLOAD_DOCUMENT",
        "DOCUMENT",
        uploadedDoc.document_id,
        {
          document_name: file.name,
          document_type: file.type,
          file_size: file.size,
          consumer_id: consumerId,
          consumer_name: consumers.find((c) => c.consumer_id === consumerId)
            ?.name,
          user_id: userId,
          authorization_id: authorizationId,
          notes: notes,
          contains_phi: !!consumerId, // Documents linked to consumers contain PHI
        }
      );

      setSuccessMessage("Document uploaded successfully!");
      setErrorMessage("");

      // Reset form
      setFile(null);
      setConsumerId("");
      setUserId("");
      setAuthorizationId("");
      setNotes("");

      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.value = "";
      }

      toast.success("Document uploaded successfully!");

      // Call the callback to switch back to document list and refresh
      if (onUploadSuccess) {
        onUploadSuccess();
      }

      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error uploading document:", error);

      // Log failed upload attempt
      await auditApi.logActionSafe("UPLOAD_DOCUMENT", "DOCUMENT", null, {
        document_name: file?.name,
        error: error.message,
        action_status: "FAILED",
        consumer_id: consumerId,
      });

      setErrorMessage("Failed to upload document. Please try again.");
      setSuccessMessage("");
      toast.error("Failed to upload document");

      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {successMessage && (
        <div className="alert alert-success" role="alert">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          {errorMessage}
        </div>
      )}

      <p className="text-muted small mb-3"><span className="text-danger">*</span> Required fields</p>

      <div className="mb-3">
        <label className="form-label">Select Document File <span className="text-danger">*</span></label>
        <input
          type="file"
          className="form-control"
          onChange={(e) => setFile(e.target.files[0])}
          required
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
        />
        <div className="form-text">
          Accepted formats: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, JPEG
        </div>
      </div>

      <div className="row">
        <div className="col-md-4 mb-3">
          <label className="form-label">Consumer <span className="text-danger">*</span></label>
          <select
            className="form-select"
            value={consumerId}
            onChange={(e) => {
              const selectedId = e.target.value;
              setConsumerId(selectedId);
              const selectedConsumer = consumers.find(
                (c) => c.consumer_id === selectedId
              );
              if (selectedConsumer?.counselor_id) {
                setUserId(selectedConsumer.counselor_id);
              }
            }}
            required
          >
            <option value="">Select Consumer</option>
            {consumers.map((consumer) => (
              <option key={consumer.consumer_id} value={consumer.consumer_id}>
                {consumer.name}{" "}
                {consumer.counselor_name ? ` - ${consumer.counselor_name}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label">Job Coach (or User) <span className="text-danger">*</span></label>
          <select
            className="form-select"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            required
          >
            <option value="">Select User</option>
            {users.map((user) => (
              <option key={user.user_id} value={user.user_id}>
                {user.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label">Authorization Number</label>
          <select
            className="form-select"
            value={authorizationId}
            onChange={(e) => setAuthorizationId(e.target.value)}
            
          >
            <option value="">Link Authorization</option>
            {authorizations.map((auths) => (
              <option
                key={auths.authorization_id}
                value={auths.authorization_id}
                
              >
                {auths.authorization_number}
              </option>
            ))}
            
          </select>
        </div>

        <div className="col-md-12 mb-3">
          <label className="form-label">Notes</label>
          <textarea
            className="form-control"
            rows="3"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Enter any additional notes about this document..."
            maxLength="500"
          ></textarea>
          <div className="form-text">{notes.length}/500 characters</div>
        </div>
      </div>

      {/* HIPAA Notice for uploads */}
      <div className="alert alert-info small mb-3">
        <i className="bi bi-info-circle-fill me-2"></i>
        <strong>HIPAA Notice:</strong> Documents linked to consumers may contain
        Protected Health Information (PHI). All document uploads are logged and
        audited for compliance.
      </div>

      <div className="d-grid">
        <button
          type="submit"
          className="btn btn-success"
          disabled={isSubmitting || !file}
        >
          {isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>{" "}
              Uploading...
            </>
          ) : (
            "Upload Document"
          )}
        </button>
      </div>
    </form>
  );
};

export default UploadDocumentForm;